import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export type TrainingVideoId = 'intro';

export interface TrainingVideoConfig {
    youtubeId: string;
    storageKey: string;
    title: string;
    description: string;
    icon: string;
}

export const TRAINING_VIDEOS: Record<TrainingVideoId, TrainingVideoConfig> = {
    intro: {
        youtubeId: 'ZV4Y14Ho2Dg',
        storageKey: 'oz_training_video_intro_dismissed',
        title: 'Introduction to Specsfin',
        description: 'Learn the basics of using the Specsfin vendor portal',
        icon: 'play_circle',
    },
};

const SYSTEM_STORAGE_KEYS = {
    VIEW_COUNT: 'oz_training_video_view_count',
    LAST_LOGIN: 'oz_training_video_last_login',
} as const;

const MAX_VIEW_COUNT: number = 3; // Maximum number of times to show the video per cycle.

const RESET_PERIOD_DAYS: number = 21; // Days after which the view cycle resets.

@Injectable({
    providedIn: 'root'
})
export class TrainingVideoService {

    /**
     * SSR-safe reference to the local storage.
     */
    private localStorageRef: Storage | undefined = this.document.defaultView?.localStorage;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly sanitizer: DomSanitizer
    ) { }

    /**
     * Returns the sanitized YouTube embed URL for the training video with autoplay.
     */
    public getYouTubeEmbedUrl(id: TrainingVideoId): SafeResourceUrl {
        const embedUrl = `https://www.youtube.com/embed/${TRAINING_VIDEOS[id].youtubeId}?autoplay=1&rel=0`;

        return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    }

    /**
     * Returns the sanitized YouTube embed URL for the training video without autoplay.
     */
    public getYouTubeEmbedUrlNoAutoplay(id: TrainingVideoId): SafeResourceUrl {
        const embedUrl = `https://www.youtube.com/embed/${TRAINING_VIDEOS[id].youtubeId}?rel=0`;

        return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    }

    /**
     * Determines if the training video popup should be shown based on:
     * - User hasn't dismissed it
     * - View count is less than maximum
     * - 21-day reset cycle
     */
    public shouldShowTrainingVideo(): boolean {
        // return false; // currently disabled.

        if (!this.localStorageRef) {
            return false;
        }

        // Check for 21-day reset first
        this.checkAndResetCycle();

        // Don't show if user has dismissed
        const dismissed: string | null = this.localStorageRef.getItem(TRAINING_VIDEOS.intro.storageKey);
        if (dismissed === 'true') {
            return false;
        }

        // Don't show if view count exceeds maximum
        const viewCount: number = this.getViewCount();
        if (viewCount >= MAX_VIEW_COUNT) {
            return false;
        }

        return true;
    }

    /**
     * Records that the video was shown (increments view count).
     */
    public recordVideoShown(): void {
        if (!this.localStorageRef) {
            return;
        }

        const currentCount: number = this.getViewCount();

        this.localStorageRef.setItem(SYSTEM_STORAGE_KEYS.VIEW_COUNT, String(currentCount + 1));
    }

    /**
     * Marks the video as dismissed (user checked "Don't show again").
     */
    public dismissVideo(id: TrainingVideoId): void {
        if (!this.localStorageRef) {
            return;
        }

        this.localStorageRef.setItem(TRAINING_VIDEOS[id].storageKey, 'true');
    }

    /**
     * Records the current login timestamp.
     */
    public recordLogin(): void {
        if (!this.localStorageRef) {
            return;
        }

        this.localStorageRef.setItem(SYSTEM_STORAGE_KEYS.LAST_LOGIN, new Date().toISOString());
    }

    /**
     * Gets the current view count.
     */
    private getViewCount(): number {
        if (!this.localStorageRef) {
            return 0;
        }

        const count: string | null = this.localStorageRef.getItem(SYSTEM_STORAGE_KEYS.VIEW_COUNT);

        return count
            ? parseInt(count, 10)
            : 0;
    }

    /**
     * Checks if 21 days have passed since last login and resets the cycle if so.
     */
    private checkAndResetCycle(): void {
        if (!this.localStorageRef) {
            return;
        }

        const lastLoginStr: string | null = this.localStorageRef.getItem(SYSTEM_STORAGE_KEYS.LAST_LOGIN);

        if (!lastLoginStr) {
            // First time - record login and continue
            this.recordLogin();
            return;
        }

        const lastLogin: Date = new Date(lastLoginStr);

        const now: Date = new Date();

        const daysSinceLastLogin: number = this.daysBetween(lastLogin, now);

        if (daysSinceLastLogin >= RESET_PERIOD_DAYS) {
            this.resetViewCycle();
        }
    }

    /**
     * Resets the view cycle (view count and dismissed flag).
     */
    private resetViewCycle(): void {
        if (!this.localStorageRef) {
            return;
        }

        this.localStorageRef.setItem(SYSTEM_STORAGE_KEYS.VIEW_COUNT, '0');

        this.localStorageRef.removeItem(TRAINING_VIDEOS.intro.storageKey);

        this.recordLogin();
    }

    /**
     * Calculates the number of days between two dates.
     */
    private daysBetween(date1: Date, date2: Date): number {
        const oneDay: number = 24 * 60 * 60 * 1000; // hours * minutes * seconds * milliseconds

        const diffMs: number = Math.abs(date2.getTime() - date1.getTime());

        return Math.floor(diffMs / oneDay);
    }

    /**
     * Checks if a specific training module is complete.
     */
    public isModuleComplete(id: TrainingVideoId): boolean {
        if (!this.localStorageRef) {
            return false;
        }

        return this.localStorageRef.getItem(TRAINING_VIDEOS[id].storageKey) === 'true';
    }

    /**
     * Marks a specific training module as complete.
     */
    public markModuleComplete(id: TrainingVideoId): void {
        if (!this.localStorageRef) {
            return;
        }

        this.localStorageRef.setItem(TRAINING_VIDEOS[id].storageKey, 'true');
    }

    /**
     * Returns the configuration for a specific video.
     */
    public getVideoConfig(id: TrainingVideoId): TrainingVideoConfig {
        return TRAINING_VIDEOS[id];
    }

    /**
     * Returns all video configurations with their IDs.
     */
    public getAllVideoConfigs(): { id: TrainingVideoId; config: TrainingVideoConfig; }[] {
        return (Object.keys(TRAINING_VIDEOS) as TrainingVideoId[]).map(id => ({
            id,
            config: TRAINING_VIDEOS[id]
        }));
    }
}
